import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpService } from 'src/app/core/services/api/http.service';
import { Auto_Complete } from 'src/app/core/services/auto-complete';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { ThirdPartiesService } from 'src/app/modules/third-parties/third-parties.service';
import { InputFieldType } from '../../models/input-types.enum';
import { ThirdPartyTypeInterface } from '../../models/third-party-type.interface';
import { ThirdPartiesApiService } from 'src/app/core/services/api/third-parties-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { API_VERSIONS } from '../../consts/api-versions.const';

@UntilDestroy()
@Component({
  selector: 'app-create-third-party-modal',
  templateUrl: './create-third-party-modal.component.html',
  styleUrls: ['./create-third-party-modal.component.scss'],
})
export class CreateThirdPartyModalComponent implements OnInit {
  @ViewChild('messageComponent', { static: false }) messageComponent: any;
  @ViewChild('deleteMsgComponentThirdParty', { static: false })
  deleteMsgComponentThirdParty: any;
  @ViewChild('customFieldComponent', { static: false })
  customFieldComponent: any;
  @ViewChild('deleteMsgComponent', { static: false })
  deleteMsgComponent: any;
  dialogThirdPartyTitle = 'Add New Third Party';
  allThirdPartiesTypes: ThirdPartyTypeInterface[] = [];
  loadingSubmit: boolean = false;
  loadingConfirm: boolean = false;
  loadingDelete: boolean = false;
  isEditThirdPartyDialog: boolean = false;
  isLoadingType: boolean = false;
  thirdPartyTypeId: number | undefined = 0;
  thirdPartyId: number | undefined = 0;
  typeAutoComplete: Auto_Complete = new Auto_Complete(
    this._api,
    this.errHandle,
    `/${API_VERSIONS.TYPES}/types`
  );
  titleAutoComplete: Auto_Complete = new Auto_Complete(
    this._api,
    this.errHandle,
    `/${API_VERSIONS.TITLES}/titles`,
    true
  );

  protected readonly InputFieldType = InputFieldType;

  constructor(
    private _api: HttpService,
    private notificationService: NotificationsService,
    private errHandle: ErrorHandlingService,
    private thirdPartiesService: ThirdPartiesService,
    public _pService: PermissionsService,
    private thirdPartiesApiService: ThirdPartiesApiService,
    private dynamicDialogConfig: DynamicDialogConfig,
    protected dynamicDialogRef: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    this.isEditThirdPartyDialog =
      this.dynamicDialogConfig.data.isEditThirdPartyDialog;
    this.thirdPartyId = this.dynamicDialogConfig.data.thirdPartyId;
    this.thirdPartyTypeId = this.dynamicDialogConfig.data.thirdPartyTypeId;
    this.getTypes();
  }

  getTypes() {
    this.isLoadingType = true;
    this.thirdPartiesApiService
      .getAllTypes()
      .pipe(
        untilDestroyed(this),
        finalize(() => (this.isLoadingType = false))
      )
      .subscribe((res) => {
        this.allThirdPartiesTypes = res || [];
      });
  }

  get formAddress(): FormGroup {
    return this.thirdPartyForm.get('formAddress') as FormGroup;
  }
  get thirdPartyTypeForm() {
    return this.thirdPartiesService.getThirdPartyTypeForm();
  }
  get thirdPartyForm() {
    return this.thirdPartiesService.getThirdPartyForm();
  }

  accepted() {
    this.messageComponent.close();
    this.thirdPartyForm.reset();
    this.thirdPartyForm.markAsUntouched();
    this.dynamicDialogRef.close();
  }

  onHide() {
    this.closeModalOutside('');
  }

  closeOutsideModal(event: any) {
    if (this.thirdPartyForm.dirty || this.thirdPartyTypeForm.dirty) {
      this.messageComponent.open();
    } else {
      this.accepted();
    }
  }

  closeModalOutside(event: any) {
    // if the form not dirty it's not important to show the discard message
    if (this.thirdPartyTypeForm.dirty || this.thirdPartyForm.dirty) {
      this.messageComponent.open();
    } else {
      this.accepted();
    }
  }
  convertToUSPhoneNumber(usPhoneNumber: string): string {
    // Remove all non-digit characters from the US phone number
    const cleanedNumber = '+1' + usPhoneNumber.replace(/\D/g, '');
    return cleanedNumber;
  }

  addEditThirdParty() {
    if (this.dynamicDialogConfig.data.type_of_add != null) {
      this.thirdPartyForm.patchValue({
        type: { id: this.dynamicDialogConfig.data.type_id },
      });
    }
    if (this.thirdPartyForm.invalid || !this.thirdPartyForm.touched) {
      this.thirdPartyForm.markAllAsTouched();
      return;
    }
    this.loadingSubmit = true;
    const streetAddress = this.thirdPartyForm?.value?.formAddress?.street_address;
    let payload: any = {
      name: this.thirdPartyForm?.value?.name,
      type_id:
        this.thirdPartyForm?.value?.type?.id ||
        this.thirdPartyForm?.value?.type,
      // code: this.thirdPartyForm.value.code,
      internal_code: this.thirdPartyForm?.value?.internal_code,
      title_id: this.thirdPartyForm?.value?.title?.id,
      first_name: this.thirdPartyForm?.value?.first_name,
      // middle_name:this.thirdPartyForm?.value?.middle_name,
      last_name: this.thirdPartyForm?.value?.last_name,
      email: this.thirdPartyForm?.value?.email,
      mobile: this.convertToUSPhoneNumber(this.thirdPartyForm?.value?.phone),
      url: this.thirdPartyForm?.value?.web_url,
      country: this.thirdPartyForm?.value?.formAddress?.country,
      state: this.thirdPartyForm?.value?.formAddress?.state,
      city: this.thirdPartyForm?.value?.formAddress?.city,
      zip_code: this.thirdPartyForm?.value?.formAddress?.zip_code,
      address: streetAddress?.label || streetAddress,
      status: this.thirdPartyForm?.value?.status?.name,
    };

    let custom_fields: any[] =
      this.customFieldComponent?.fetchFormArrayData() || [];

    if (custom_fields.length > 0) {
      payload['custom_fields'] = custom_fields;
    }

    if (!this.isEditThirdPartyDialog) {
      this._api.postReq('/third-parties', payload).subscribe(
        (res) => {
          this.loadingSubmit = false;
          this.notificationService.success('', res?.message);
          this.thirdPartiesService.invokeUpdatedThirdPartyMethod(
            'add',
            'thirdParty'
          );
          this.thirdPartyForm.reset();
          this.thirdPartyForm.markAsUntouched();
          this.dynamicDialogRef.close(true);
        },
        (err) => {
          this.errHandle.errorHandling(err);
          this.loadingSubmit = false;
        }
      );
    } else {
      this._api
        .putReq(
          `/third-parties/${this.dynamicDialogConfig.data.thirdPartyId}`,
          payload
        )
        .subscribe(
          (res) => {
            this.loadingSubmit = false;
            this.notificationService.success('', res?.message);
            this.thirdPartiesService.invokeUpdatedThirdPartyMethod(
              'updated',
              'thirdParty'
            );
            this.thirdPartyForm.reset();
            this.thirdPartyForm.markAsUntouched();
            this.dynamicDialogRef.close(true);
          },
          (err) => {
            this.errHandle.errorHandling(err);
            this.loadingSubmit = false;
          }
        );
    }
  }
  stopProp(event: any) {
    event.stopPropagation();
  }

  rejected() {
    this.messageComponent.close();
  }

  // delete third party
  deleteThirdParty() {
    this.loadingDelete = true;
    this._api.deleteReq(`/third-parties/${this.thirdPartyId}`).subscribe(
      (res) => {
        this.loadingDelete = false;
        this.notificationService.success('', res?.message);
        // this.showThirdPartiesPopUp = false;
        this.accepted();
        this.thirdPartiesService.invokeUpdatedThirdPartyMethod(
          'delete',
          'thirdParty'
        );
      },
      (err) => {
        this.errHandle.errorHandling(err);
        this.loadingDelete = false;
      }
    );
    this.deleteMsgComponentThirdParty.close();
  }

  // delete third party type
  deleteThirdPartyType() {
    this.loadingConfirm = true;
    this._api.deleteReq(`/types/${this.thirdPartyTypeId}`).subscribe(
      (res) => {
        this.loadingConfirm = false;
        this.notificationService.success('', res?.message);
        // this.showThirdPartiesTypePopUp = false;
        this.thirdPartyTypeForm.markAsUntouched();
        this.deleteMsgComponent.close();
        this.accepted();
        this.thirdPartiesService.invokeUpdatedThirdPartyMethod(
          'delete',
          'type'
        );
      },
      (err) => {
        this.errHandle.errorHandling(err);
        this.loadingConfirm = false;
      }
    );
  }
}
